import styles from "./CircularTaskProgress.module.scss";

const SIZE = 14;
const CX = SIZE / 2;
const CY = SIZE / 2;
const RING_STROKE = 2;
const RING_R = SIZE / 2 - 1;
const SECTOR_GAP = 2;
const SECTOR_R = RING_R - RING_STROKE / 2 - SECTOR_GAP;

function getSectorPath(pct: number): string {
    const startRad = -Math.PI / 2;
    const endRad = startRad + (2 * Math.PI * pct) / 100;

    if (pct >= 100) {
        // A single arc cannot form a full circle; use two half-arcs.
        const top = `M ${CX} ${CY - SECTOR_R}`;
        const half = `A ${SECTOR_R} ${SECTOR_R} 0 1 1 ${CX} ${CY + SECTOR_R}`;
        const back = `A ${SECTOR_R} ${SECTOR_R} 0 1 1 ${CX} ${CY - SECTOR_R}`;
        return `${top} ${half} ${back} Z`;
    }

    const x1 = (CX + SECTOR_R * Math.cos(startRad)).toFixed(4);
    const y1 = (CY + SECTOR_R * Math.sin(startRad)).toFixed(4);
    const x2 = (CX + SECTOR_R * Math.cos(endRad)).toFixed(4);
    const y2 = (CY + SECTOR_R * Math.sin(endRad)).toFixed(4);
    const largeArc = pct > 50 ? 1 : 0;

    return `M ${CX} ${CY} L ${x1} ${y1} A ${SECTOR_R} ${SECTOR_R} 0 ${largeArc} 1 ${x2} ${y2} Z`;
}

interface CircularTaskProgressProps {
    percentage: number;
}

export const CircularTaskProgress = ({
    percentage,
}: Readonly<CircularTaskProgressProps>) => {
    const clampedPct = Math.min(100, Math.max(0, percentage));

    return (
        <svg
            className={styles.root}
            width={SIZE}
            height={SIZE}
            viewBox={`0 0 ${SIZE} ${SIZE}`}
            role="img"
            aria-label={`${clampedPct}% complete`}
        >
            <circle
                className={styles.track}
                cx={CX}
                cy={CY}
                r={RING_R}
                strokeWidth={RING_STROKE}
            />
            {clampedPct > 0 && (
                <path className={styles.fill} d={getSectorPath(clampedPct)} />
            )}
        </svg>
    );
};
